import React, { useEffect, useMemo, useState } from 'react';
import * as Icons from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { NAV_GROUPS } from './navConfig';
import { Sidebar, Rail, IconBtn, GroupHeader, Items, SubItem, Drawer, Hamburger, LogoWrap, LogoImg, ProfileWrap, ProfileButton, Avatar, ProfileText, LogoutInline } from './NavStyles';
import { useAuth } from '../../context/AuthContext';
import { filterNavGroups, getEffectivePermissions } from '../../auth/permissions';

const useNavMode = () => {
  const [mode, setMode] = useState('expanded'); // expanded | collapsed | mobile
  useEffect(() => {
    const mqMobile = window.matchMedia('(max-width: 767px)');

    const getAvailWidth = () => {
      const s = window.screen;
      const w = (s?.availWidth || s?.width || 0);
      return w > 0 ? w : window.innerWidth;
    };

    const apply = () => {
      if (mqMobile.matches) return setMode('mobile');

      // Ratio-based collapse: handles Windows 11 snapping (e.g. half-screen on 4K)
      const avail = getAvailWidth();
      const ratio = avail > 0 ? (window.innerWidth / avail) : 1;
      if (ratio <= 0.55) return setMode('collapsed');
      return setMode('expanded');
    };

    apply();

    const onResize = () => apply();
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('orientationchange', onResize, { passive: true });

    const onMobileChange = () => apply();
    mqMobile.addEventListener('change', onMobileChange);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      mqMobile.removeEventListener('change', onMobileChange);
    };
  }, []);
  return mode;
};

const NavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const mode = useNavMode();
  const { user, logout } = useAuth();
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('nav-open') || '{}'); } catch { return {}; }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showLogout, setShowLogout] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    const width = mode === 'expanded' ? '240px' : mode === 'collapsed' ? '64px' : '0px';
    root.style.setProperty('--nav-width', width);
    return () => {
      root.style.removeProperty('--nav-width');
    };
  }, [mode]);

  useEffect(() => {
    try { localStorage.setItem('nav-open', JSON.stringify(openGroups)); } catch {}
  }, [openGroups]);

  const currentGroupKey = useMemo(() => {
    for (const g of NAV_GROUPS) {
      if (location.pathname === g.path) return g.key;
      if (g.items?.some(i => location.pathname.startsWith(i.path))) return g.key;
    }
    return null;
  }, [location.pathname]);

  const renderIcon = (name) => {
    const Cmp = Icons[name] || Icons.Circle;
    return <Cmp size={20} />;
  };

  const go = (path) => {
    navigate(path);
    if (mode === 'mobile') setDrawerOpen(false);
  };

  const filteredGroups = useMemo(() => {
    if (!user) return [];
    const effective = getEffectivePermissions(user, {
      mockAdmin: process.env.REACT_APP_MOCK_ADMIN_USER === 'true',
    });
    return filterNavGroups(NAV_GROUPS, effective);
  }, [user]);

  const initials = (user?.name || user?.email || 'U').split(/\s+/).map(s => s[0]).slice(0,2).join('').toUpperCase();
  const [avatarOk, setAvatarOk] = useState(true);
  
  const handleLogout = async () => {
    try { 
      setShowLogout(false); 
    } catch {}
    
    try {
      await logout();
    } catch (err) {
      console.error('Logout failed:', err);
      // Fallback: clear state and redirect
      window.location.href = '/';
    }
  };

  if (mode === 'mobile') {
    return (
      <>
        <Hamburger aria-label={drawerOpen ? 'Close menu' : 'Open menu'} aria-expanded={drawerOpen} onClick={() => setDrawerOpen(v => !v)}>
          {drawerOpen ? <Icons.X size={18} /> : <Icons.Menu size={18} />}
        </Hamburger>
        <Drawer $open={drawerOpen} onClick={() => setDrawerOpen(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <Rail>
              {filteredGroups.map(g => (
                <IconBtn key={g.key} aria-label={g.label} aria-current={currentGroupKey === g.key ? 'page' : undefined} onClick={() => go(g.path)}>
                  {renderIcon(g.icon)}
                </IconBtn>
              ))}
            </Rail>
            <ProfileWrap $expanded={showLogout}>
              <ProfileButton aria-label="Account" onClick={() => setShowLogout(v => !v)}>
                <Avatar aria-hidden>
                  {user?.photoUrl && avatarOk ? (
                    <img src={user.photoUrl} alt="avatar" onError={() => setAvatarOk(false)} />
                  ) : (
                    initials
                  )}
                </Avatar>
              </ProfileButton>
              {showLogout && (
                <LogoutInline onClick={handleLogout}>
                  <Icons.LogOut size={18} />
                </LogoutInline>
              )}
            </ProfileWrap>
          </div>
        </Drawer>
      </>
    );
  }

  if (mode === 'collapsed') {
    return (
      <Sidebar $mode={mode}>
        <LogoWrap $mode={mode}>
          <LogoImg $mode={mode} src="/BlackKRP.png" alt="The Vault" />
        </LogoWrap>
        <Rail>
          {filteredGroups.map(g => (
            <IconBtn key={g.key} aria-label={g.label} aria-current={currentGroupKey === g.key ? 'page' : undefined} onClick={() => go(g.path)}>
              {renderIcon(g.icon)}
            </IconBtn>
          ))}
        </Rail>
        <ProfileWrap $expanded={showLogout}>
          <ProfileButton aria-label="Account" onClick={() => setShowLogout(v => !v)}>
            <Avatar aria-hidden>{initials}</Avatar>
          </ProfileButton>
          {showLogout && (
            <LogoutInline onClick={handleLogout}>
              <Icons.LogOut size={18} />
            </LogoutInline>
          )}
        </ProfileWrap>
      </Sidebar>
    );
  }

  // expanded
  return (
      <Sidebar $mode={mode}>
      <LogoWrap $mode={mode}>
        <LogoImg $mode={mode} src="/BlackKRP.png" alt="The Vault" />
      </LogoWrap>
      <Rail>
        {filteredGroups.map(g => {
          if (!g.items?.length) {
            return (
              <IconBtn key={g.key} aria-label={g.label} aria-current={location.pathname === g.path ? 'page' : undefined} onClick={() => go(g.path)}>
                {renderIcon(g.icon)}
                <span>{g.label}</span>
              </IconBtn>
            );
          }
          const isOpen = !!openGroups[g.key];
          return (
            <div key={g.key}>
              <GroupHeader role="button" aria-expanded={isOpen} onClick={() => setOpenGroups(prev => ({ ...prev, [g.key]: !prev[g.key] }))}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {renderIcon(g.icon)}
                  <span>{g.label}</span>
                </div>
                {isOpen ? <Icons.ChevronUp size={16} /> : <Icons.ChevronDown size={16} />}
              </GroupHeader>
              {isOpen && (
                <Items>
                  {g.items.map(it => (
                    <SubItem key={it.path} onClick={() => go(it.path)} aria-current={location.pathname === it.path ? 'page' : undefined}>
                      {it.label}
                    </SubItem>
                  ))}
                </Items>
              )}
            </div>
          );
        })}
      </Rail>
        {/* Profile section at bottom (expanded) */}
        <ProfileWrap $expanded={showLogout}>
          <ProfileButton aria-label="Account" onClick={() => setShowLogout(v => !v)}>
            <Avatar aria-hidden>
              {user?.photoUrl && avatarOk ? (
                <img src={user.photoUrl} alt="avatar" onError={() => setAvatarOk(false)} />
              ) : (
                initials
              )}
            </Avatar>
            <ProfileText>
              <strong>{user?.name || 'User'}</strong>
              <span>{user?.email || ''}</span>
            </ProfileText>
          </ProfileButton>
          {showLogout && (
            <LogoutInline onClick={handleLogout}>
              <Icons.LogOut size={18} /> Logout
            </LogoutInline>
          )}
        </ProfileWrap>
    </Sidebar>
  );
};

export default React.memo(NavBar);


